import classNames from 'classnames/bind';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareAlt } from '@fortawesome/pro-solid-svg-icons/faShareAlt';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faSquare } from '@fortawesome/pro-light-svg-icons/faSquare';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons/faSquareCheck';

import {
  UK_PRIVATE_COMPANY_INSIGHTS_BREADCRUMBS, UK_PRIVATE_COMPANY_INSIGHTS_PATH,
  singleInvestorFields, UK_PRIVATE_COMPANY_INSIGHTS_PATH_MAIN, UKDisableMessage,
} from '../../../data/ukPrivateCompanyInsights';
import InsiderSvc from '../../../services/dbServices/InsiderSvc';
import loadable from '../../../utils/loadable';
import getUserToken from '../../../helpers/getUserToken';
import { setAuthModalOpened } from '../../../actions/authentication.actions';
import { perPageFilterDataTablet } from '../../../data/directory/publications/publications';
import { removeSlugFromPath } from '../../../helpers/topicsHelpers';
import { filterByStoryOrder, filterByTimePeriod } from '../../../actions/storyFilters.action';
import { ALLOWED_TABS, DEFAULT_TABS, TABS } from '../../../data/directory/topic_classes/constants';
import { PRIVATE_INVESTOR } from '../../../data/directory/topic_classes/topicClassTypes';
import { getPageMetaData } from '../../../services/dbServices/userServices';
import {
  loadAllWatchlists,
  loadNewWatchlist,
} from '../../../services/dbServices/watchlistDbServices';
import { getMetaTag } from '../../../helpers/helpers';
import { removeDuplicates } from '../../../helpers/removeDuplicates';
import { formatTopicsQuery } from '../../../helpers/watchlistHelperFunctions';
import { UK_INSIGHTS, UK_INSIGHTS_TRIAL } from '../../../data/features';
import { sortDataFunction } from '../../../helpers/ukInsightsHelpers';
import { handleSingleInvestorsFilter } from '../../../actions/ukPrivateCompany.actions';
import { GOLD } from '../../../data/permissions';

import Breadcrumbs from '../../Breadcrumbs';
import PerPage from '../../SEO_pages/PremiumPublications/Pagination/PerPage';
import TopicTab from '../../SEO_pages/TopicTabBodyCommon/TopicTab';
import Investments from './Investments';
import StakeholdersSvc from '../../../services/dbServices/StakeholdersSvc';
import convertPermalinkToPath from '../../../helpers/urlHelpers';
import Tooltip from '../../Tooltip';
import WatchlistCreateModal from '../../Modals/WatchlistCreateModal';
import ConditionalTooltip from '../CommonComponent/ConditionalTooltip';

import Styles from './styles.module.scss';
import { createNewPrivateCompaniesForWatchlist } from '../../../helpers/topicsApiHelpers';

const ShareModal = loadable(() => import('../../TrendingStories/StoryCard/StoryCardFooter/ShareModal'));

const cx = classNames.bind(Styles);

const SingleInvestor = ({
  nameSlug,
  history,
  location,
  match,
}) => {
  let mounted = true;
  const initialized = useRef(false);
  const tabsBar = useRef(false);
  const currentWatchlistNames = useRef(new Set());
  const mainWidth = useSelector(({ common }) => (
    common.width
  ));
  const maxWatchlistSize = useSelector(({ subscriptions }) => (
    subscriptions.permissions.max_watchlist_size
  ));

  const dispatch = useDispatch();
  const [singleInvestorData, setSingleInvestorData] = useState({
    data: [],
  });

  const [showWlNameModal, setShowWlNameModal] = useState(false);
  const [isWlNameTaken, setIsWlNameTaken] = useState(false);
  const [includeSimilarNames, setIncludeSimilarNames] = useState(true);
  const [newWlName, setNewWlName] = useState('');
  const [watchlistState, setWatchlistState] = useState('initial');
  const watchlistsCountRef = useRef(1);

  const [totalValue, setTotalValue] = useState({});
  const [tabs, setTabs] = useState(() => TABS.filter((f) => f.isDefault));
  const [activeTabId, setActieTabId] = useState(1);
  const [loading, setLoading] = useState(true);
  const [newLoading, setNewLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [investorName, setInvestorName] = useState('');
  const [investorID, setInvestorID] = useState('');
  const pageTitleArray = nameSlug.split('-').map((pageTitleElement) => pageTitleElement[0].toUpperCase() + pageTitleElement.slice(1));
  const pageTitle = pageTitleArray.join(' ');

  const hasCSVExportAccess = useSelector(({ subscriptions }) => (
    subscriptions.permissions.access_levels?.includes(GOLD)
  ));
  const hasInsightsAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS)
  ));
  const hasInsightsTrialAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS_TRIAL)
  )) && !hasInsightsAccess;

  const SINGLE_INVESTOR_BREADCRUMBS = [
    {
      name: 'Directory Home',
      path: 'news/directory',
    },
    ...UK_PRIVATE_COMPANY_INSIGHTS_BREADCRUMBS,
    {
      name: investorName,
      path: `${UK_PRIVATE_COMPANY_INSIGHTS_PATH}/entities/${nameSlug}`,
    },
  ];
  let shareRef = useRef();

  const handleShowShareModal = () => {
    if (!getUserToken()) return dispatch(setAuthModalOpened(true));

    shareRef.handleShow({
      id: nameSlug,
      title: `${window.location.origin}/${UK_PRIVATE_COMPANY_INSIGHTS_PATH_MAIN}/entities/${nameSlug}`,
      url: `${window.location.origin}/${UK_PRIVATE_COMPANY_INSIGHTS_PATH_MAIN}/entities/${nameSlug}`,
    });
  };

  useEffect(() => {
    loadAllWatchlists({
      onlyWatchlistNames: true,
    }).then((watchlists) => {
      currentWatchlistNames.current = new Set(watchlists.map((wl) => wl.name));
    });
  }, []);

  useEffect(() => {
    StakeholdersSvc.searchStakeholders({ slug: nameSlug })
      .then((resData) => {
        const parsedData = removeDuplicates([
          ...(resData.fuzzy || []),
          ...(resData.regular || []),
        ], 'slug');
        const data = parsedData.find((f) => f.slug === nameSlug);
        setInvestorName(data?.name || pageTitle);
        setNewWlName(data?.name || pageTitle);
        setInvestorID(data?.id);
        getPageMetaData('~directory/companies-insights/uk/entities/%/investments')
          .then((metaData) => {
            Object.values(metaData).forEach(({ title, meta_tags: metatags }) => {
              if (!metatags || !metatags.length) {
                return;
              }
              const hasTitle = metatags.find((f) => f.name === 'title');
              if (!hasTitle) {
                metatags.push({
                  name: 'title',
                  content: title,
                });
              }
              for (let i = 0; i < metatags.length; i += 1) {
                const { name, content, property } = metatags[i];
                const formatedText = content
                  .replace('%{asset_name}', data?.name);
                if (name === 'title') {
                  document.title = formatedText;
                }
                if (property) {
                  getMetaTag('property', property).setAttribute('content', formatedText);
                } else {
                  getMetaTag('name', name).setAttribute('content', formatedText);
                }
              }
            });
          });
      });
  }, [nameSlug]);

  const callAPI = () => {
    if (!investorID) return;
    const params = {
      id: investorID,
      with_similar_investors: includeSimilarNames || undefined,
      with_similar_investors_data: includeSimilarNames || undefined,
    };
    setNewLoading(!loading);
    setLoading(true);

    InsiderSvc.getCompanySingleInvestor(params)
      .then((res) => {
        if (!mounted) {
          return;
        }
        const { investments, totalValueOfHoldings } = res || {};

        initialized.current = true;
        setTotalValue({
          companyName: 'Total',
          valueOfHoldings: totalValueOfHoldings || 0,
        });
        const sortData = sortField
          ? sortDataFunction(investments, sortField, sortOrder) : investments;
        setSingleInvestorData({
          data: (sortData || []).map((obj) => ({
            ...obj,
            url: obj.companyPermalink ? convertPermalinkToPath(obj.companyPermalink) : '',
          })),
        });

        setLoading(false);
        setNewLoading(false);
      });
  };

  const changeTab = (id) => {
    const allTabs = tabs.map((tab) => ({
      ...tab,
      active: tab.id === id,
    }));
    const { path } = allTabs.find((tab) => tab.active === true);
    const basePath = removeSlugFromPath(location.pathname);
    history.push(`${basePath}/${path}`);
    setTabs(allTabs);
    setActieTabId(id);
  };

  useEffect(() => {
    callAPI();
    const { params: urlParams } = match;

    const { tabs: allowedTabs } = ALLOWED_TABS.find(
      ({ type }) => type === PRIVATE_INVESTOR,
    ) || DEFAULT_TABS;

    const tabs = (TABS
      .filter((tab) => allowedTabs.includes(tab.id))
      .map((tab) => ({ ...tab, active: tab.path === urlParams.page })));
    const { id } = tabs.filter((tab) => tab.active === true);
    setTabs(tabs);
    setActieTabId(id);

    window.scrollTo(0, 0);

    return () => {
      mounted = false;
    };
  }, [match.params.page, investorID, includeSimilarNames]);

  useEffect(() => {
    if (!match.params.page) {
      history.push(`${location.pathname}/investments`);
    }
  }, []);

  useEffect(() => {
    if (!initialized.current) return;
    callAPI();
  }, [investorID]);

  const hasNextPage = (singleInvestorData.data || []).length > (currentPage * Number(pageSize));

  useEffect(() => {
    dispatch(handleSingleInvestorsFilter({
      sortField,
      sortOrder,
    }));
  }, [sortField, sortOrder]);

  const onSort = (field) => {
    if (!hasInsightsAccess || hasInsightsTrialAccess) return;

    let newSortOrder = sortOrder;

    if (sortField === field) {
      newSortOrder = newSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newSortOrder = 'asc';
    }
    const data = sortDataFunction(singleInvestorData.data, field, newSortOrder);
    setSingleInvestorData({
      data,
    });
    setSortOrder(newSortOrder);
    setSortField(field);
  };

  const pageSizeChange = (size) => {
    if (!hasInsightsAccess) return;
    setPageSize(size);
    setCurrentPage(1);
  };

  const changePage = (type) => {
    if (loading || newLoading || !hasInsightsAccess) return;
    if (type === 'prev') {
      if (currentPage === 1) return;
      setCurrentPage(currentPage - 1);
    } else {
      if (!hasNextPage) return;
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    dispatch(filterByStoryOrder('top'));
    dispatch(filterByTimePeriod({ time_filter: 'week1' }));
  }, []);

  const resetModal = useCallback(() => {
    setShowWlNameModal(false);
    setNewWlName(investorName);
    setIsWlNameTaken(false);
    setWatchlistState('initial');
  }, [investorName]);

  const createWatchlist = async () => {
    if (!singleInvestorData.data.length || !newWlName || watchlistState !== 'initial') {
      return;
    }

    if (currentWatchlistNames.current.has(newWlName)) {
      setIsWlNameTaken(true);
      return;
    }

    try {
      setWatchlistState('creating');

      const companyNumbers = [...new Set(singleInvestorData.data.map((d) => d.companyNumber))];
      for (let i = 0; i < companyNumbers.length / maxWatchlistSize; i += 1) {
        const companiesForWl = companyNumbers.slice(
          i * maxWatchlistSize,
          (i + 1) * maxWatchlistSize,
        );
        const companies = await Promise.all(
          Array.from({ length: Math.ceil(companiesForWl.length / 10) }).map((_, index) => (
            InsiderSvc.getCompanySearch({
              identifier: companiesForWl.slice(index * 10, (index + 1) * 10).map((id) => `NO:${id}`).join(','),
            })
          )),
        ).then((res) => res.flatMap((r) => r.regular || []).filter((f) => f.source === 'companieshouse-gb'));

        const createdCompanies = await createNewPrivateCompaniesForWatchlist(
          companies.map((c) => ({ ...c, isNewCompanies: true })),
        );
        const wlName = companyNumbers.length > maxWatchlistSize ? `${newWlName}${i + 1}` : newWlName;
        await loadNewWatchlist(
          undefined,
          formatTopicsQuery(createdCompanies),
          wlName,
        );
        currentWatchlistNames.current.add(wlName);
      }
      watchlistsCountRef.current = Math.ceil(companyNumbers.length / maxWatchlistSize);
    } finally {
      setWatchlistState('created');
    }
  };

  const disableMessage = !hasCSVExportAccess
    ? (
      <>
        These are available only to Gold subscribers.
        {' '}
        <Link to="/pricing">
          Upgrade
        </Link>
        {' '}
        to use these features.
      </>
    )
    : UKDisableMessage(hasInsightsTrialAccess);

  const renderContent = () => (
    <div className={cx('container')}>
      <div className={cx('single_investor_header')}>
        <div className={cx('single_investor_header_title')}>
          Current Holdings
        </div>
        <div className={cx('include_similar_names')}>
          <input
            className="d-none"
            id="include-similar-names"
            type="checkbox"
            checked={includeSimilarNames}
            onChange={() => setIncludeSimilarNames((current) => !current)}
          />
          <label htmlFor="include-similar-names">
            <FontAwesomeIcon className={cx('icon', 'unchecked')} icon={faSquare} />
            <FontAwesomeIcon className={cx('icon', 'checked')} icon={faSquareCheck} />
          </label>
          <span className={cx('label')}>
            Include similar names
          </span>
          <Tooltip
            hasBigScreenDesign
            tooltipClassName={cx('tooltip')}
          >
            Include names that are closely related.
            These may be spelling and formatting variations,
            or it may be other people with similar names.
          </Tooltip>
        </div>
        <div className={cx('single_investor_actions')}>
          <div className={Styles['create-wl']}>
            <ConditionalTooltip
              tooltipProps={{
                hasBigScreenDesign: true,
                isCentered: true,
                className: cx('create-wl-button-tooltip'),
              }}
              tooltipMessage={disableMessage}
              showTooltip={!hasInsightsAccess && !hasCSVExportAccess}
            >
              <button
                type="button"
                className={cx('create-wl-button', { disable: !hasInsightsAccess && !hasCSVExportAccess })}
                onClick={() => hasInsightsAccess && hasCSVExportAccess && setShowWlNameModal(true)}
              >
                Create Watchlist
                <span>for all the topics</span>
              </button>
            </ConditionalTooltip>
            <Tooltip
              tooltipClassName={Styles['create-wl-tooltip-content']}
              contentStyle={{ left: 0 }}
              dynamicPosition
              hasBigScreenDesign
            >
              One or multiple watchlists will be created for
              all the companies on this page.
              Each watchlist will have up to&nbsp;
              {maxWatchlistSize}
              &nbsp;companies.
            </Tooltip>
          </div>
          <div className={cx('single_investor_share')} onClick={handleShowShareModal}>
            <FontAwesomeIcon icon={faShareAlt} />
            Share This List
          </div>
        </div>
      </div>
      <Investments
        isLoading={loading}
        singleInvestorData={
          singleInvestorData.data
            .slice(
              ((currentPage - 1) * Number(pageSize)),
              (((currentPage - 1) * Number(pageSize)) + Number(pageSize)),
            )
        }
        singleInvestorFields={singleInvestorFields}
        mainWidth={mainWidth}
        totalValue={totalValue}
        currentPage={currentPage}
        pageSize={pageSize}
        hasInsightsAccess={hasInsightsAccess || hasInsightsTrialAccess}
        sortOrder={sortOrder}
        sortField={sortField}
        onSort={onSort}
        hasInsightsTrialAccess={hasInsightsTrialAccess}
      />
      <div className={cx('single_investor_pagination')}>
        <PerPage
          selectedValue={pageSize}
          setSelectedValue={pageSizeChange}
          fixedSize
          fixedSizeFilterData={perPageFilterDataTablet}
          isDisable={!hasInsightsAccess}
          tooltipProps={{
            hasBigScreenDesign: true,
            isCentered: true,
            dynamicPosition: true,
            contentStyle: { left: 0 },
            className: cx('single_investor_page_tooltip'),
          }}
          tooltipMessage={disableMessage}
          tooltipDisable={!hasInsightsAccess}
        />
        <div className={cx('single_investor_navigation')}>
          <div className={cx('single_investor_label')}>
            Page:
          </div>
          <ConditionalTooltip
            tooltipProps={{
              hasBigScreenDesign: true,
              isCentered: true,
              dynamicPosition: true,
              contentStyle: { left: 0 },
              className: cx('single_investor_tooltip'),
            }}
            tooltipMessage={disableMessage}
            showTooltip={!hasInsightsAccess}
          >
            <div
              className={cx('single_investor_prev', {
                disable: currentPage === 1 || loading || newLoading || !hasInsightsAccess,
              })}
              onClick={() => changePage('prev')}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              Prev.
            </div>
          </ConditionalTooltip>
          <ConditionalTooltip
            tooltipProps={{
              hasBigScreenDesign: true,
              isCentered: true,
              dynamicPosition: true,
              contentStyle: { left: 0 },
              className: cx('single_investor_tooltip'),
            }}
            tooltipMessage={disableMessage}
            showTooltip={!hasInsightsAccess}
          >
            <div
              className={cx('single_investor_next', {
                disable: !hasNextPage || loading || newLoading || !hasInsightsAccess,
              })}
              onClick={() => changePage('next')}
            >
              Next
              <FontAwesomeIcon icon={faChevronRight} />
            </div>
          </ConditionalTooltip>
        </div>
      </div>
      <ShareModal
        ref={(el) => { shareRef = el; }}
        isNotStoryPage
        hasBigScreenDesign
      />
      <WatchlistCreateModal
        show={showWlNameModal}
        state={watchlistState}
        resetModal={resetModal}
        createWatchlist={createWatchlist}
        isWlNameTaken={isWlNameTaken}
        value={newWlName}
        setValue={setNewWlName}
        numberOfCreated={watchlistsCountRef.current}
      />
    </div>
  );

  return (
    <div className={cx('single_investor')}>
      <div className={cx('container')}>
        <Breadcrumbs
          base=""
          items={SINGLE_INVESTOR_BREADCRUMBS}
        />
        <div className={cx('single_investor_name')}>
          {investorName}
        </div>
        <div className={cx('single_investor_note')}>
          Data is machine-extracted. Please verify before making any decisions.
          <Tooltip
            newIcon
            hasBigScreenDesign
            dynamicPosition
            contentStyle={{ left: 0 }}
            className={cx('single_investor_note_tooltip')}
          >
            The data presented here is automatically extracted from the relevant documents
            filled by the companies. Some data may be inaccurately extracted.
            Accuracy and completeness should be verified before making decisions.
            {' '}
            <a href="/contact-us" rel="noreferrer">
              Contact us
            </a>
            {' '}
            to notify us of errors and concerns.
          </Tooltip>
        </div>
      </div>

      <TopicTab
        tabs={tabs}
        changeTab={changeTab}
        activeTab={activeTabId}
        ref={tabsBar}
        className={cx('single_investor_tabs')}
      />
      { renderContent() }
    </div>
  );
};

export default SingleInvestor;
